//This is needed to extend the Window interface with nhsapp properties
declare global {
	interface Window {
		nhsapp?: {
			tools?: {
				isOpenInNHSApp: () => Promise<boolean> | boolean;
			};
			navigation?: {
				AppPage: { [key: string]: string };
				goToPage: (page: string) => void;
				setBackAction: (callback: () => void) => void;
			};
		};
	}
}

import { useFeatureFlag } from '@application/feature_flags/use_feature_flag';
import React, { createContext, useContext, useEffect, useState } from 'react';

export const NhsAppContext = createContext<{ isInNHSApp: boolean | null; backAction: () => void }>({
	isInNHSApp: null,
	// biome-ignore lint/suspicious/noEmptyBlockStatements: suppressed due to migration - should be fixed
	backAction: () => {},
});

export const NhsAppProvider = ({ children }: { children?: React.ReactNode }) => {
	const [isInNHSApp, setIsInNHSApp] = useState<boolean | null>(null);
	const BACK_URL = 'GO_BACK'; // According to NHS App docs this is supposed to route back to previous NHS App page
	const hasNhsNavigationFeature = useFeatureFlag('PatientWeb.NHS.Navigation');

	const nhsBackAction = () => {
		if (window.nhsapp?.navigation?.goToPage) {
			window.nhsapp?.navigation.goToPage(window.nhsapp?.navigation.AppPage[BACK_URL]);
		}
	};

	// biome-ignore lint/correctness/useExhaustiveDependencies: suppressed due to migration - should be fixed
	useEffect(() => {
		if (hasNhsNavigationFeature) {
			const checkContext = async () => {
				try {
					if (window.nhsapp?.tools?.isOpenInNHSApp) {
						const result = await window.nhsapp.tools.isOpenInNHSApp();
						setIsInNHSApp(result);
					} else {
						console.log('NHS App context not loaded');
					}

					if (window.nhsapp?.navigation?.setBackAction) {
						window.nhsapp.navigation.setBackAction(nhsBackAction); // Sets native back action in Android and iOS NHS App
					}
				} catch (err) {
					console.error('Error checking NHS App context:', err);
					setIsInNHSApp(false);
				}
			};
			checkContext();
		}
	}, [hasNhsNavigationFeature]);

	return <NhsAppContext.Provider value={{ isInNHSApp, backAction: nhsBackAction }}>{children}</NhsAppContext.Provider>;
};

export const useNHSApp = () => useContext(NhsAppContext);
